import styled from "styled-components";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { TInfiniteScroller, TOptGroupOptions, TVariants } from ".";
import Search from "../Inputs/Search";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { TAccount } from "../../../redux/reducers/account";
import {
  getQuerystringParams,
  handleGetDataFromLocalStorage,
} from "../../../utils/miscellaneous";
import { localStorageConstants } from "../../../utils/constants";
import Spinner from "../Spinner";
import { cnpjMask } from "../../../utils/formatters";
import {
  setOrganizations,
  setOrganizationsOnSearch,
  setPreviouslyLoadedOrganizations,
  TOrganization,
} from "../../../redux/reducers/organizations";
import React from "react";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";
import TimedInfiniteScroller from "../../TimedInfiniteScroller";
import apiService from "../../../services/api";
import axios, { CancelTokenSource } from "axios";

const Container = styled.menu<{
  $shouldshow: "show" | "hide";
  $variant: TVariants;
  $hasSearch?: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  max-height: 450px;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px;
  border: 1px solid var(--color-dark-blue-300);
  padding: ${({ $variant }) => ($variant === "labeled" ? "8" : "8")}px;
  margin: 0;
  position: absolute;
  height: ${({ $shouldshow }) => ($shouldshow === "show" ? "auto" : "0")};
  opacity: ${({ $shouldshow }) => ($shouldshow === "show" ? 1 : 0)};
  visibility: ${({ $shouldshow }) =>
    $shouldshow === "show" ? "visible" : "hidden"};
  z-index: ${({ $shouldshow }) => ($shouldshow === "show" ? "2" : "-1")};
  transition: z-index 0.15s, visibility 0.15s, height 0.2s, opacity 0.2s;
  background: var(--color-light-0);

  div.spinnerContainer {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  & button {
    margin-top: 4px;

    &:nth-child(1) {
      width: 100%;
      ${({ $hasSearch }) => ($hasSearch ? "margin-top: 8px;" : "")}
    }

    &[id^="search-"] {
      margin-top: 0px;
    }
  }

  & button:nth-child(1),
  & p.optGroup {
    visibility: ${({ $shouldshow }) =>
      $shouldshow === "show" ? "visible" : "hidden"};
    transition: visibility 0.1s;
  }

  & .optGroupContainer {
    position: relative;
    height: 31px;
  }

  & p.optGroup {
    position: absolute;
    z-index: 1;
    top: 25%;
    left: 12px;
    font-family: var(--font-paragraph-default);
    font-size: 12px;
    color: var(--color-dark-blue-400);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: ${({ $shouldshow }) => ($shouldshow === "show" ? "100%" : "0%")};
    width: 100%;
    background: var(--color-light-0);
    border-radius: 8px;
    transition: height 0.2s;
    pointer-events: none;
  }

  p.content,
  & button.active {
    visibility: ${({ $shouldshow }) =>
      $shouldshow === "show" ? "visible" : "hidden"};
  }
`;

const ItemButton = styled.button<{
  $shouldHideActiveIcon?: boolean;
  $label?: string;
  $value?: string;
  $variant: TVariants;
  $shouldshow: "show" | "hide";
  $labelRect?: DOMRect;
}>`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  min-height: 46px;
  height: ${({ $labelRect }) =>
    $labelRect && $labelRect.height > 17 ? $labelRect.height + 30 : 46}px;
  width: 100%;
  text-align: start;
  background: var(--color-light-0);
  border-radius: 8px;
  visibility: ${({ $shouldshow }) =>
    $shouldshow === "show" ? "visible" : "hidden"};
  transition: visibility 0.1s ease-in-out;

  ${({ $label }) =>
    $label &&
    `&::before, & .before{
      max-width: 230px;
      content:'${$label}';
      font-size: 14px;
      font-family: var(--font-paragraph-bold);
      color: var(--color-text-paragraph);
      position: absolute;
      top: 7px;
      left: 12px;
    }` + `& .before { visibility: hidden;}`}

  p.content {
    ${({ $variant, $labelRect }) =>
      $variant === "unlabeled"
        ? `
        font-family: var(--font-paragraph-default);
        color: var(--color-text-paragraph);
        font-size: 14px;
      `
        : `
        font-family: var(--font-paragraph-default);
        color: var(--color-text-labels);
        font-size: 12px;` +
          `top: ${
            $labelRect && $labelRect.height > 17 ? $labelRect.height + 11 : 26
          }px;`}
    width: calc(100% - 40px);
    position: absolute;
    left: 12px;
  }

  div.naturalPadding {
    visibility: hidden;
    &::before {
      content: "${({ $value }) => $value}";
      width: calc(100% - 40px);
      font-size: 14px;
    }
  }

  .spinner-right {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    pointer-events: none;
  }

  &:hover {
    background: var(--color-light-200);
  }

  &:disabled {
    opacity: 0.5;
  }

  &.active,
  &:active {
    visibility: ${({ $shouldshow }) =>
      $shouldshow === "show" ? "visible" : "hidden"};
    transition: visibility 0.1s ease-in-out;
    background: var(--color-main-blue);
    &::before,
    p {
      color: var(--color-light-0);
    }
  }
`;

type TCommonProps = {
  shouldHideActiveIcon?: boolean;
  list: "default" | "filter";
  content: string[];
  onClick: (
    selectedIndex: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    value?: string
  ) => void;
  shouldShow: "show" | "hide";
  searchId?: string;
  searchPhraseOnChange?: (searchPhrase: string) => void;
  searchPhraseOnSearch?: (searchPhrase: string) => void;
  optGroup?: TOptGroupOptions[];
  forceSelectOptionsByIndex?: number[];
  hasInfiniteScrolling?: TInfiniteScroller;
  setData?: (
    setSelectedData: React.Dispatch<React.SetStateAction<string[]>>
  ) => void;
  disabledOptions?: Array<string | number>; // option values or indices to disable
  itemLoading?: Record<string, boolean>;
  setItemLoading?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
};

type TLabeled = {
  variant: "labeled";
  label: string[];
};

type TUnlabeled = {
  variant: "unlabeled";
  label?: never;
};

type TMenuProps<Variant extends TVariants> = Variant extends "labeled"
  ? TLabeled & TCommonProps
  : TUnlabeled & TCommonProps;

const Menu: React.FC<TMenuProps<TVariants>> = ({
  variant,
  shouldHideActiveIcon,
  content,
  label,
  onClick,
  shouldShow,
  searchId,
  optGroup,
  forceSelectOptionsByIndex,
  searchPhraseOnChange,
  searchPhraseOnSearch,
  hasInfiniteScrolling,
  setData,
  disabledOptions,
  itemLoading,
}) => {
  const ITEMS_TO_RENDER_AMOUNT = 10;

  const selectOrganization = useTypedSelector(
    (state) => state.organizations.selectedOrganization
  );

  const dispatch = useTypedDispatch();

  const spinnerId = useId();

  const [orgsToRender, setOrgsToRender] = useState<TOrganization[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [selected, setSelected] = useState([""]);

  const selectedValueRef = useRef("");
  const elementsAmountRef = useRef(0);

  const [hasMore, setHasMore] = useState(true);

  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const cancelTokenOnSearchRef = useRef<CancelTokenSource | null>(null);

  // Ensure contentToRender is initialized with content for non-paginated lists (e.g., natureOptions)
  const [contentToRender, setContentToRender] = useState<string[]>(
    content.length > 0 ? content : []
  );
  const [labelToRender, setLabelToRender] = useState<string[]>([]);
  const [searchPhrase, setSearchPhrase] = useState<string | undefined>();

  const [labelRects, setLabelRects] = useState<DOMRect[]>([]);
  const labelRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const [renderTick, setRenderTick] = useState(0);

  const pageRef = useRef(1);

  const forceSelectOptionsByIndexRef = useRef<number[] | undefined>(
    forceSelectOptionsByIndex
  );

  const [, /* selectAccount */ setSelectAccount] = useState<TAccount | null>(
    null
  );

  const clickHandler = (
    _e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number | string,
    value?: string
  ) => {
    onClick(
      typeof index === "string" ? index : `${index}`,
      selected,
      setSelected,
      value
    );
  };

  const searchPhraseOnChangeHandler = (searchPhrase: string) => {
    setSelected([]);

    if (searchPhrase.length > 0) {
      setSearchPhrase(searchPhrase);
      if (searchPhraseOnChange) searchPhraseOnChange(searchPhrase);
      setContentToRender([]);
      setLabelToRender([]);
      pageRef.current = 1;
      setHasMore(true);
    } else {
      // If search phrase is cleared (empty string), trigger the same logic as onClear
      setSearchPhrase(undefined);
      setContentToRender(content);
      setLabelToRender(label ?? []);
      if (searchPhraseOnChange) searchPhraseOnChange("");
    }
  };

  const handleRenderMenu = useCallback(() => {
    if (
      hasInfiniteScrolling &&
      hasInfiniteScrolling.variant === "organizations"
    ) {
      function getMergedContentAndLabels(): {
        mergedContent: string[];
        mergedLabels: (string | undefined)[];
      } {
        const cnpjToName = new Map<string, string>();
        contentToRender.forEach((cnpj, i) => {
          if (!cnpjToName.has(cnpj)) {
            cnpjToName.set(cnpj, labelToRender[i]);
          }
        });
        const mergedContent = orgsToRender.map((org) => cnpjMask(org.cnpj));
        const mergedLabels = orgsToRender.map((org) => org.company_name);
        return { mergedContent, mergedLabels };
      }

      const { mergedContent, mergedLabels } = getMergedContentAndLabels();

      const querystring = getQuerystringParams();

      const loadMore = () => {
        setIsLoading(true);

        if (cancelTokenRef.current || cancelTokenOnSearchRef.current) {
          apiService
            .getOrganizations(
              querystring.account_id,
              searchPhrase && searchPhrase.length > 0
                ? (
                    cancelTokenOnSearchRef.current ??
                    (cancelTokenOnSearchRef.current =
                      axios.CancelToken.source())
                  ).token
                : (
                    cancelTokenRef.current ??
                    (cancelTokenRef.current = axios.CancelToken.source())
                  ).token,
              pageRef.current,
              searchPhrase
            )
            .then((response) => {
              const newData = response.data.results;
              // Merge loaded organizations and last accessed organizations, deduplicating by uuid
              dispatch(setPreviouslyLoadedOrganizations(newData));

              if (searchPhrase && searchPhrase.length > 0) {
                dispatch(setOrganizationsOnSearch(newData));
              } else {
                const existingResults =
                  (window as any).store?.getState()?.organizations?.data
                    ?.results || [];
                const lastAccessedOrgs =
                  (window as any).store?.getState()?.organizations
                    ?.lastAccessedOrganizations || [];
                const mergedResults = [
                  ...existingResults,
                  ...newData,
                  ...lastAccessedOrgs,
                ].reduce((acc: TOrganization[], org: TOrganization) => {
                  if (!acc.some((o) => o.uuid === org.uuid)) acc.push(org);
                  return acc;
                }, []);
                dispatch(
                  setOrganizations({
                    ...((window as any).store?.getState()?.organizations
                      ?.data || {}),
                    results: mergedResults,
                  })
                );
              }

              const hasSearchPhrase = searchPhrase && searchPhrase.length > 0;

              const currentPage = pageRef.current;

              setContentToRender((prev) => {
                const all =
                  hasSearchPhrase && currentPage === 1
                    ? newData.map((org: any) => org.cnpj)
                    : [...prev, ...newData.map((org: any) => org.cnpj)];
                return Array.from(new Set(all));
              });
              setLabelToRender((prev) =>
                hasSearchPhrase && currentPage === 1
                  ? [...newData.map((org: any) => org.company_name)]
                  : [...prev, ...newData.map((org: any) => org.company_name)]
              );

              setOrgsToRender((prev) => {
                const all =
                  hasSearchPhrase && currentPage === 1
                    ? newData
                    : [...prev, ...newData];

                const unique = all.reduce(
                  (acc: TOrganization[], org: TOrganization) => {
                    if (!acc.some((o) => o.uuid === org.uuid)) acc.push(org);
                    return acc;
                  },
                  []
                );
                return unique;
              });

              pageRef.current += 1;

              setHasMore(response.data.pages[pageRef.current] !== undefined);
            })
            .catch(() => {
              pageRef.current = 1;
              setHasMore(false);
              setIsLoading(false);
            })
            .finally(() => {
              setIsLoading(false);
            });
        }
      };

      return (
        <>
          {shouldShow === "show" && (
            <TimedInfiniteScroller
              loadMore={loadMore}
              hasMore={hasMore}
              isLoading={isLoading}
              loaderComponent={
                <Spinner
                  style={{
                    height: hasMore ? "unset" : "0px",
                  }}
                  id={spinnerId}
                />
              }
              endMessage={<span>{/* No more organizations */}</span>}
            >
              {mergedContent.length > 0 &&
                mergedContent.map((value, index) => {
                  setTimeout(() => {
                    const itemButtonComponent = document.getElementById(
                      "itemButton-" + value
                    );
                    const contentClassElement = document.getElementById(
                      "content-" + value
                    );
                    if (
                      itemButtonComponent &&
                      itemButtonComponent.style.visibility !== "visible" &&
                      shouldShow === "show"
                    ) {
                      itemButtonComponent.style.visibility = "visible";
                    }
                    if (labelRects[index] && labelRects[index].height > 17) {
                      if (contentClassElement && itemButtonComponent) {
                        const beforeClassElementHeight =
                          labelRects[index].height;
                        if (
                          itemButtonComponent.getBoundingClientRect().height <
                          beforeClassElementHeight + 30
                        ) {
                          itemButtonComponent.style.height =
                            beforeClassElementHeight + 30 + "px";
                        }
                        if (
                          contentClassElement.getBoundingClientRect().height <
                          beforeClassElementHeight + 11
                        ) {
                          contentClassElement.style.top =
                            beforeClassElementHeight + 11 + "px";
                        }
                      }
                    } else {
                      if (contentClassElement && itemButtonComponent) {
                        itemButtonComponent.style.height = 46 + "px";
                        contentClassElement.style.top = 26 + "px";
                      }
                    }
                  }, 50);

                  return (
                    <ItemButton
                      $shouldHideActiveIcon={shouldHideActiveIcon}
                      $shouldshow={shouldShow}
                      id={"itemButton-" + value}
                      className={
                        // If any item is loading, only the loading item is active
                        itemLoading && Object.values(itemLoading).some(Boolean)
                          ? itemLoading[value]
                            ? "active"
                            : ""
                          : forceSelectOptionsByIndexRef.current?.includes(
                              index
                            ) ||
                            selected.includes(`${index}`) ||
                            selectOrganization?.cnpj?.replace(/\D/g, "") ===
                              String(value).replace(/\D/g, "")
                          ? "active"
                          : ""
                      }
                      $label={mergedLabels && mergedLabels[index]}
                      $value={value}
                      $variant={variant}
                      key={index}
                      onClick={(e) => {
                        clickHandler(e, index, mergedContent[index]);
                      }}
                      $labelRect={labelRects[index]}
                      disabled={
                        disabledOptions?.includes(value) ||
                        disabledOptions?.includes(index)
                      }
                    >
                      {mergedLabels && (
                        <p
                          ref={(ref) => {
                            labelRefs.current[index] = ref;
                          }}
                          id={value}
                          className="before"
                        >
                          {mergedLabels[index]}
                        </p>
                      )}
                      <p id={"content-" + value} className="content">
                        {value}
                      </p>
                      <div className="naturalPadding">{value}</div>
                      {itemLoading && itemLoading[value] && (
                        <span className="spinner-right">
                          <Spinner
                            style={{ width: 16, height: 16 }}
                            color="white"
                          />
                        </span>
                      )}
                    </ItemButton>
                  );
                })}
            </TimedInfiniteScroller>
          )}
        </>
      );
    }

    // If no infinite scrolling, always ensure contentToRender is initialized with content
    const items =
      content.length > ITEMS_TO_RENDER_AMOUNT
        ? contentToRender.length > 0
          ? contentToRender
          : content
        : content;

    return (
      <>
        {items.map((value, index) => {
          setTimeout(() => {
            const itemButtonComponent = document.getElementById(
              "itemButton3-" + value
            );
            const contentClassElement = document.getElementById(
              "content3-" + value
            );
            if (labelRects[index] && labelRects[index].height > 17) {
              if (contentClassElement && itemButtonComponent) {
                const beforeClassElementHeight = labelRects[index].height;
                if (
                  itemButtonComponent.getBoundingClientRect().height <
                  beforeClassElementHeight + 30
                ) {
                  itemButtonComponent.style.height =
                    beforeClassElementHeight + 30 + "px";
                }
              }
            } else {
              if (
                contentClassElement &&
                itemButtonComponent &&
                !shouldHideActiveIcon
              ) {
                itemButtonComponent.style.height = 46 + "px";
              }
            }
          }, 50);
          return (
            <ItemButton
              $shouldHideActiveIcon={shouldHideActiveIcon}
              id={"itemButton3-" + value}
              $shouldshow={shouldShow}
              className={
                forceSelectOptionsByIndexRef.current?.includes(index) ||
                selected.includes(value)
                  ? "active"
                  : ""
              }
              $label={label && label[index]}
              $value={value}
              $variant={variant}
              key={index}
              onClick={(e) => {
                selectedValueRef.current = value;
                clickHandler(e, index, value);
                forceSelectOptionsByIndexRef.current = [];
              }}
              $labelRect={labelRects[index]}
              disabled={
                disabledOptions?.includes(value) ||
                disabledOptions?.includes(index)
              }
            >
              <p
                ref={(ref) => {
                  labelRefs.current[index] = ref;
                }}
                id={"content3-" + value}
                className={
                  "content" +
                  (disabledOptions?.includes(value) ||
                  disabledOptions?.includes(index)
                    ? " disabled"
                    : "")
                }
              >
                {value}
              </p>
              <div className="naturalPadding">{value}</div>
              {itemLoading && itemLoading[value] && (
                <span className="spinner-right">
                  <Spinner style={{ width: 16, height: 16 }} />
                </span>
              )}
            </ItemButton>
          );
        })}

        {hasInfiniteScrolling !== undefined && content && (
          <TimedInfiniteScroller
            delay={3000}
            loadMore={() => {
              let filteredContent = content;
              if (searchPhrase && searchPhrase.length > 0) {
                filteredContent = content.filter((item) =>
                  item.toLowerCase().includes(searchPhrase.toLowerCase())
                );
                if (contentToRender.length === 0) {
                  setContentToRender(
                    filteredContent.slice(0, ITEMS_TO_RENDER_AMOUNT)
                  );
                  elementsAmountRef.current = Math.min(
                    ITEMS_TO_RENDER_AMOUNT,
                    filteredContent.length
                  );
                  return;
                }
              }
              const nextItems = filteredContent.slice(
                contentToRender.length,
                contentToRender.length + ITEMS_TO_RENDER_AMOUNT
              );
              setContentToRender([...contentToRender, ...nextItems]);
              elementsAmountRef.current =
                contentToRender.length + nextItems.length;
            }}
            hasMore={(() => {
              let filteredContent = content;
              if (searchPhrase && searchPhrase.length > 0) {
                filteredContent = content.filter((item) =>
                  item.toLowerCase().includes(searchPhrase.toLowerCase())
                );
              }
              return contentToRender.length < filteredContent.length;
            })()}
            isLoading={false}
            loaderComponent={
              <Spinner
                style={{ height: "unset", width: "unset" }}
                id={spinnerId}
              />
            }
            endMessage={<span></span>}
          >
            <></>
          </TimedInfiniteScroller>
        )}
      </>
    );
  }, [
    hasInfiniteScrolling,
    labelRects,
    contentToRender,
    forceSelectOptionsByIndexRef,
    selected,
    selectOrganization,
    labelToRender,
    optGroup,
    content,
    label,
    shouldShow,
  ]);

  useEffect(() => {
    setRenderTick((tick) => tick + 1);
  }, [contentToRender, labelToRender, shouldShow]);

  useLayoutEffect(() => {
    const newRects = labelRefs.current.map((ref) =>
      ref ? ref.getBoundingClientRect() : new DOMRect()
    );
    if (
      newRects.length !== labelRects.length ||
      newRects.some(
        (rect, i) =>
          !labelRects[i] ||
          rect.width !== labelRects[i].width ||
          rect.height !== labelRects[i].height
      )
    ) {
      setLabelRects(newRects);
    }
  }, [renderTick]);

  useEffect(() => {
    cancelTokenRef.current = axios.CancelToken.source();
    const localSelectAccount = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ACCOUNT
    );
    if (localSelectAccount) setSelectAccount(localSelectAccount);

    if (setData) setData(setSelected);

    return () => {
      cancelTokenRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    if (
      hasInfiniteScrolling &&
      hasInfiniteScrolling.variant !== "nonPaginated"
    ) {
      if (
        ((contentToRender.length === 0 && content.length > 0) ||
          (contentToRender.length > 0 &&
            contentToRender.length < content.length)) &&
        (!searchPhrase || searchPhrase.length === 0)
      ) {
        setContentToRender(content);
      }

      if (
        label &&
        ((labelToRender.length === 0 && label.length > 0) ||
          (labelToRender.length > 0 && labelToRender.length < label.length)) &&
        (!searchPhrase || searchPhrase.length === 0)
      ) {
        setLabelToRender(label);
      }
    }
  }, [content, label]);

  return (
    <Container
      $hasSearch={searchId !== undefined}
      $variant={variant}
      $shouldshow={shouldShow}
    >
      {searchId && searchId.length > 0 && (
        <Search
          placeholder="Pesquisar"
          id={"search-" + searchId}
          variant="small"
          onChange={searchPhraseOnChangeHandler}
          onSearch={searchPhraseOnSearch}
          onClear={() => {
            if (hasInfiniteScrolling?.variant !== "organizations") {
              if (selectedValueRef.current.length > 0) {
                setSelected([`${content.indexOf(selectedValueRef.current)}`]);
              }
            } else {
              setSelected([]);
              pageRef.current = 1;
              setHasMore(true);
              setOrgsToRender([]);
            }
            setSearchPhrase(undefined);
            setContentToRender([]);
            setLabelToRender([]);
            if (searchPhraseOnChange) searchPhraseOnChange("");
          }}
        />
      )}

      {handleRenderMenu()}
    </Container>
  );
};

export default React.memo(Menu);
